import styled from 'styled-components';
import { Box, Typography, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';

// Styles for IntroBox
export const SBox = styled(Box)`
  background-color: #FFFFFF;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  /* Matches StopperDeveloper's 520px: the two cards sit one above the other
     in the wizard, so a narrower box here left them visibly misaligned. */
  max-width: 520px;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: auto;
  @media (max-width: 768px) {
    max-width: 350px;
  }
`;

export const SLabelTop = styled.label`
  color: #60646E;
  font-size: 12px;
  font-family: 'IRANYekanXFaNum';
  font-weight: 500;
  text-align: center;
  margin: 20px 0 16px 2px;
  line-height: 15.6px;
`;

export const SLinkContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 1px;
`;

export const SClickableText = styled.span`
  cursor: pointer;
  color: #60646E;
  font-size: 12px;
  font-family: 'IRANYekanXFaNum';
  font-weight: 500;
  &:hover {
    text-decoration: underline;
  }
`;

export const SDivider = styled.hr`
  width: 100%;
  border: 0.5px solid #ccc;
  margin: 16px 0;
  border: 1px #D9D9DD dotted;
  margin: 2px 0 12px 20px;
`;

export const SsubTitle = styled.h6`
  margin: 2px 0 12px 12px;
  color: #60646E;
  font-size: 14px;
  font-family: 'IRANYekanXFaNum';
  font-weight: 500;
  line-height: 20px;
  text-align: right;
`;

export const STitle = styled.div`
  color: White & Black/10;
  font-size: 20px;
  font-family: 'IRANYekanXFaNum';
  font-weight: 900;
  margin: 2px 0 12px 20px;
  text-align: right;
  line-height: 26px;
`;

export const Sitem = styled.div`
  color: White & Black/10;
  font-size: 14px;
  font-family: 'IRANYekanXFaNum';
  font-weight: 600;
  margin: 2px 0 12px 20px;
  text-align: right;
  line-height: 18.2px;
  align-items: right;
`;

export const SuBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  // Without an explicit width this shrank to its content under SBox's
  // `align-items: center`, letting each card size itself independently.
  width: '100%',
  // stretch, not center: the two cards must share one width, not each take
  // the width of its own longest label.
  alignItems: 'stretch',
  gap: '16px',
});

// Styles for IdentityFormBox
export const ClickableBox = styled(Box)({
  // Width comes from SuBox, which stretches both cards to one size. A
  // max-width here would let the shorter card stop early again.
  width: '100%',
  height: 'auto',
  padding: '12px',
  backgroundColor: '#FFFFFF',
  border: '1px solid #E5E5E8',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  '&:hover': {
    borderColor: '#C6C6DF',
    boxShadow: '0 2px 8px rgba(16, 24, 40, 0.06)',
  },
});

// The two chips read as one pair: the identity name in brand blue, the
// legal-status qualifier in a quieter neutral beside it.
export const SecondaryTextA = styled(Typography)({
  fontFamily: 'IRANYekanXFaNum',
  fontSize: 14,
  color: '#4994ec',
  backgroundColor: '#EAF3FD',
  fontWeight: 700,
  lineHeight: 1.6,
  textAlign: 'right',
  padding: '2px 8px',
  borderRadius: '6px',
});

export const SecondaryTextB = styled(Typography)({
  fontFamily: 'IRANYekanXFaNum',
  fontSize: 14,
  color: '#60646E',
  backgroundColor: '#F2F2F5',
  fontWeight: 700,
  lineHeight: 1.6,
  textAlign: 'right',
  padding: '2px 8px',
  borderRadius: '6px',
});

export const TextRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
  justifyContent: 'flex-end',
  marginBottom: '12px',
});

// Applied to the <form> so it fills the card. Left bare, the form shrank to
// its own content, which is why the two cards did not match.
export const SFormContainer = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const SFieldContainer = styled.div`
  display: flex;
  flex-direction: row;
  /* Centre, not baseline: the helper text under an invalid field grows the
     TextField, and baseline alignment dragged the label down with it. */
  align-items: center;
  width: 100%;
  gap: 8px; // 8px gap between label and TextField
  direction: rtl;
  margin-bottom: 8px;
  justify-content: flex-end; // قرارگیری label در سمت چپ
`;

export const SLabel = styled(Typography)`
  && {
    font-family: 'IRANYekanXFaNum';
    font-size: 14px;
    font-weight: 500;
    text-align: right;
    margin-left: 4px;
    flex: 0 0 auto;
    white-space: nowrap;
    line-height: 1.2;
    min-width: 80px; // عرض ثابت برای label ها
  }
`;

export const SStyledTextField = styled(TextField)`
  && {
    /* min-width:0 lets the field shrink inside the flex row; without it the
       input's intrinsic width pushes the label out of the card. */
    flex: 1;
    min-width: 0;

    .MuiOutlinedInput-root {
      width: 100%;
      border-radius: 8px;
      fieldset {
        border-color: #ededed;
      }
      &:hover fieldset {
        border-color: #cfcfcf;
      }
      &.Mui-focused fieldset {
        border-color: #afafaf;
      }
      /* Matches Identify/Styles.ts StyledTextField, the form standard. */
      input {
        padding: 12px 14px;
        font-size: 14px;
        text-align: right;
      }
    }
    .MuiFormHelperText-root {
      font-size: 12px;
      color: red;
      text-align: right;
      direction: rtl;
      margin: 4px 0 0 0;
    }
  }
`;

export const useStyles = makeStyles({
  primaryText: {
    fontFamily: 'IRANYekanXFaNum',
    fontSize: '16px',
    fontWeight: 700,
    color: '#35373C',
    lineHeight: '20.8px',
    textAlign: 'right',
  },
});